import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Link2, Zap, CheckCircle2, Clock, Users, ClipboardList, Bell, PhoneCall, Trophy, Star, Play, X, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { LandingFAQ } from "../components/app/LandingFAQ";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";

const FEATURE_IMG = "https://customer-assets.emergentagent.com/job_leads-dashboard-pro/artifacts/pnrxsi1a_cleaning-tasklio.jpeg";

// Calendly link for the "Schedule a Live Demo" CTA. Centralized so any
// future change (new Calendly slug, alternative scheduler, etc.) only
// needs to touch this constant and not every CTA in the page.
const DEMO_CALENDLY_URL = "https://calendly.com/tasklio-support/demo";

// Real product screenshots — the visual centerpiece of the new hero and
// the "See Tasklio in Action" section below. These are the SOURCE OF
// TRUTH the user uploaded; do NOT replace with stock mockups.
const SCREENSHOT_DASHBOARD =
  "https://customer-assets.emergentagent.com/job_leads-dashboard-pro/artifacts/zx3dkq1k_%20%20%20%20dashboard.png";
const SCREENSHOT_CALENDAR =
  "https://customer-assets.emergentagent.com/job_leads-dashboard-pro/artifacts/5x3qoy8n_%20%20%20%20calendar.png";
const SCREENSHOT_FINANCIALS =
  "https://customer-assets.emergentagent.com/job_leads-dashboard-pro/artifacts/rfkjeqme_%20%20%20%20financials.png";
const SCREENSHOT_QUOTES =
  "https://customer-assets.emergentagent.com/job_leads-dashboard-pro/artifacts/fpc1yv7a_quotes.png";
const SCREENSHOT_GROW =
  "https://customer-assets.emergentagent.com/job_leads-dashboard-pro/artifacts/j9330h26_grow-business.png";

// ---------------------------------------------------------------------------
// ProductShowcaseCarousel — one large, readable product screenshot at a time
// with arrows, dots, keyboard, and swipe controls. Replaces the previous 5-up
// grid that made the UI illegible. Built without an external carousel lib
// because the behavior we need is small enough that an off-the-shelf one
// would be net negative for bundle size and a11y predictability.
// ---------------------------------------------------------------------------
const ProductShowcaseCarousel = ({ t }) => {
  const slides = [
    {
      key: "dashboard",
      src: SCREENSHOT_DASHBOARD,
      alt: "Tasklio dashboard — leads, jobs, and pipeline",
      titleKey: "landing.showcase.dashboardTitle",
      bodyKey: "landing.showcase.dashboardBody",
    },
    {
      key: "calendar",
      src: SCREENSHOT_CALENDAR,
      alt: "Tasklio calendar showing booked cleaning jobs",
      titleKey: "landing.showcase.calendarTitle",
      bodyKey: "landing.showcase.calendarBody",
    },
    {
      key: "financials",
      src: SCREENSHOT_FINANCIALS,
      alt: "Tasklio financials with revenue, expenses, and net",
      titleKey: "landing.showcase.financialsTitle",
      bodyKey: "landing.showcase.financialsBody",
    },
    {
      key: "quotes",
      src: SCREENSHOT_QUOTES,
      alt: "Tasklio AI-generated reply and quote workflow",
      titleKey: "landing.showcase.quotesTitle",
      bodyKey: "landing.showcase.quotesBody",
    },
    {
      key: "grow",
      src: SCREENSHOT_GROW,
      alt: "Tasklio Grow Your Business AI tools",
      titleKey: "landing.showcase.growTitle",
      bodyKey: "landing.showcase.growBody",
    },
  ];

  const [index, setIndex] = useState(0);
  const total = slides.length;
  const trackRef = useRef(null);

  const go = (i) => setIndex(((i % total) + total) % total);
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  // Keyboard navigation: arrow keys advance/rewind when the carousel
  // (or any focused control inside it) is focused. Scoped to the
  // section ref so we don't hijack arrow keys for the whole page.
  const sectionRef = useRef(null);
  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;
    const onKey = (e) => {
      // Only react when focus is inside the carousel section. Otherwise
      // arrow keys on body would steal scroll for keyboard users.
      if (!root.contains(document.activeElement)) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      }
    };
    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
    // index is intentionally a dep so prev/next close over the latest value
  }, [index]);

  // Touch swipe — track start X on touchstart and direction on touchend.
  // Threshold of 40px prevents accidental swipes from tapping.
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const dx = endX - touchStartX.current;
    if (Math.abs(dx) >= 40) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <section
      ref={sectionRef}
      className="bg-white border-t border-gray-100"
      data-testid="product-showcase-section"
      // tabIndex so the section can receive focus → arrow keys work
      tabIndex={-1}
      aria-roledescription="carousel"
      aria-label={t("landing.showcaseTitle")}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">
            {t("landing.showcaseEyebrow")}
          </span>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight"
            data-testid="product-showcase-title"
          >
            {t("landing.showcaseTitle")}
          </h2>
          <p
            className="mt-4 text-gray-600 leading-relaxed text-base sm:text-lg"
            data-testid="product-showcase-subtitle"
          >
            {t("landing.showcaseSubtitle")}
          </p>
        </div>

        {/* Carousel viewport */}
        <div className="relative mt-10 sm:mt-12 lg:mt-16">
          <div
            ref={trackRef}
            className="relative overflow-hidden rounded-2xl"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="product-showcase-track"
            aria-live="polite"
          >
            {/* Inline-flex track that slides via translateX. Each slide
                takes the full track width so we get smooth transitions
                without per-slide measurement. */}
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {slides.map((s, i) => (
                <article
                  key={s.key}
                  className="w-full shrink-0 px-2 sm:px-4"
                  // `aria-hidden` non-active slides for SR users so they
                  // don't read all 5 titles at once.
                  aria-hidden={i !== index}
                  data-testid={`product-showcase-slide-${s.key}`}
                  data-active={i === index ? "true" : "false"}
                >
                  <div className="rounded-2xl bg-white ring-1 ring-gray-200 shadow-2xl shadow-emerald-900/10 overflow-hidden">
                    {/* Browser-chrome strip → reads as a live product
                        surface, matching the hero treatment. */}
                    <div className="flex items-center gap-1.5 px-4 h-8 border-b border-gray-100 bg-white">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-300/70" aria-hidden="true" />
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-300/70" aria-hidden="true" />
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" aria-hidden="true" />
                    </div>
                    <img
                      src={s.src}
                      alt={s.alt}
                      className="block w-full h-auto"
                      loading={i === 0 ? "eager" : "lazy"}
                      draggable={false}
                    />
                  </div>

                  {/* Caption block under the active slide. Centered and
                      generously padded so it reads like a feature page
                      callout, not a thumbnail label. */}
                  <div className="mt-6 sm:mt-8 max-w-2xl mx-auto text-center">
                    <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 tracking-tight">
                      {t(s.titleKey)}
                    </h3>
                    <p className="mt-3 text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed">
                      {t(s.bodyKey)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Prev/Next controls. Absolute on desktop (next to the slide),
              inline on mobile (under the dots). */}
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            data-testid="product-showcase-prev"
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-2 lg:-left-4 z-10 items-center justify-center w-11 h-11 rounded-full bg-white border border-gray-200 shadow-md text-gray-700 hover:text-emerald-800 hover:border-emerald-200 hover:shadow-lg transition focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            data-testid="product-showcase-next"
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-2 lg:-right-4 z-10 items-center justify-center w-11 h-11 rounded-full bg-white border border-gray-200 shadow-md text-gray-700 hover:text-emerald-800 hover:border-emerald-200 hover:shadow-lg transition focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots + mobile prev/next row */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            data-testid="product-showcase-prev-mobile"
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 active:scale-95 transition"
          >
            <ChevronLeft className="w-4.5 h-4.5" />
          </button>

          <div
            className="flex items-center gap-2"
            role="tablist"
            aria-label="Slide selector"
          >
            {slides.map((s, i) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Show ${s.key}`}
                onClick={() => go(i)}
                data-testid={`product-showcase-dot-${s.key}`}
                className={
                  "h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 " +
                  (i === index
                    ? "w-8 bg-emerald-700"
                    : "w-2 bg-gray-300 hover:bg-gray-400")
                }
              />
            ))}
          </div>

          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            data-testid="product-showcase-next-mobile"
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 active:scale-95 transition"
          >
            <ChevronRight className="w-4.5 h-4.5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  // When a user is already authenticated, we must NOT route them to /register
  // because submitting that form replaces their token (effectively logging them out
  // of their existing account). Send them straight to the dashboard instead.
  const primaryCtaTo = user ? "/dashboard" : "/register";
  const primaryCtaLabel = user ? t("nav.goDashboard") : t("nav.startTrial");

  // Hero "Watch demo" — opens a native MP4 modal. We pause the video on
  // close so the audio stops mid-scrub doesn't keep playing in the
  // background. The MP4 lives at /public/demo/tasklio-demo.mp4 so it ships
  // with the static frontend bundle (no third-party SDK on this path).
  const [demoOpen, setDemoOpen] = useState(false);
  // When the MP4 isn't uploaded yet (e.g. fresh preview pod, 404 on
  // /demo/tasklio-demo.mp4), we render a friendly "coming soon" card
  // instead of a broken <video> player. `onError` flips this to true.
  const [videoErrored, setVideoErrored] = useState(false);
  const videoRef = useRef(null);

  const onDemoOpenChange = (open) => {
    setDemoOpen(open);
    if (!open && videoRef.current) {
      // Pause + rewind so a re-open starts clean. Wrap in try because some
      // browsers throw if the video isn't ready yet (rare race).
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
  };
  return (
    <div className="min-h-screen flex flex-col" data-testid="landing-page">
      <Navbar />

      {/* Hero — split into a 2-column layout on lg+: copy on the left,
          a layered product-screenshot composition on the right. The real
          uploaded screenshots become the visual centerpiece so visitors
          immediately understand Tasklio is a full SaaS platform (not just
          a single feature). */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        {/* Soft branded gradient backdrop — replaces the previous full-bleed
            stock cleaning photo so the product imagery has visual room to
            breathe. Subtle radial accents in green keep the brand without
            overpowering the screenshots. */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-emerald-50/30 to-white" />
          <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-emerald-200/30 blur-3xl" />
          <div className="absolute top-20 right-0 w-[520px] h-[520px] rounded-full bg-green-200/25 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* LEFT — copy + CTAs */}
            <div className="lg:col-span-6 max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-semibold text-green-800 bg-green-50 border border-green-100 px-3 py-1 rounded-full">
                <Sparkles className="w-3.5 h-3.5" /> {t("landing.heroEyebrow")}
              </span>
              <h1 className="font-display mt-5 text-[2rem] sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.08]">
                {t("landing.heroTitle")}
                <span className="text-green-800">{t("landing.heroTitleAccent")}</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-gray-700 leading-relaxed">
                {t("landing.heroSubhead")}
              </p>
              {/* Trust positioning line — kept smaller and muted so it
                  reads as supporting copy under the subhead, not a second
                  headline. Reinforces vertical specificity ("for cleaning
                  businesses") which improves conversion on first scan. */}
              <p
                className="mt-2 text-sm text-gray-500"
                data-testid="hero-positioning-line"
              >
                {t("landing.heroPositioning")}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                {/* Primary CTA: book a live demo via Calendly. Cleaning
                    business owners convert significantly better after a
                    live demo than after a self-serve trial, so demo is
                    now the dominant action. Free trial stays one tap
                    away as the secondary button. */}
                <a
                  href={DEMO_CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto"
                  data-testid="hero-schedule-demo"
                >
                  <Button
                    className="bg-green-800 hover:bg-green-900 text-white h-12 px-7 text-base font-semibold rounded-lg shadow-sm w-full sm:w-auto inline-flex items-center justify-center"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {t("landing.ctaScheduleDemo")}
                  </Button>
                </a>
                <Button
                  onClick={() => navigate(primaryCtaTo)}
                  variant="outline"
                  className="h-12 px-7 text-base font-semibold rounded-lg bg-white border-gray-300 text-gray-800 hover:bg-gray-50 w-full sm:w-auto inline-flex items-center justify-center"
                  data-testid="hero-start-free"
                >
                  {user ? t("nav.goDashboard") : t("landing.ctaStartTrial")}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {/* Two checkmark trust lines: 30-day trial + no contract.
                  "English & Spanish" used to live here too — it's been
                  removed because the prominent flag pill below already
                  surfaces "English & Spanish Supported" and the duplicate
                  was visual noise. */}
              <div
                className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-700"
                data-testid="hero-trust-line"
              >
                <span className="flex items-center gap-1.5 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-green-700" /> {t("landing.trust14Days")}
                </span>
                <span className="flex items-center gap-1.5 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-green-700" /> {t("landing.trust2Min")}
                </span>
              </div>
              {/* Flag pill — reinforces the bilingual story right under
                  the hero CTAs so it can't be missed during a quick scan. */}
              <div className="mt-4" data-testid="hero-lang-badge">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-900 bg-white border border-emerald-200 rounded-full px-3 py-1 shadow-sm">
                  <span aria-hidden="true">🇺🇸</span>
                  <span aria-hidden="true">🇪🇸</span>
                  {t("landing.heroLangBadge")}
                </span>
              </div>
            </div>

            {/* RIGHT — demo booking card. Cleaning owners don't convert
                from screenshots; they convert from a 1:1 walkthrough.
                This card replaces the dashboard screenshot as the
                primary right-column unit. Hidden under lg because the
                mobile/tablet hero already has the demo CTA as the
                primary button. */}
            <div
              className="hidden lg:block lg:col-span-6"
              data-testid="hero-demo-card-wrapper"
            >
              <div
                className="relative rounded-3xl bg-gradient-to-br from-white via-white to-emerald-50/50 border border-gray-200 shadow-[0_30px_80px_-30px_rgba(6,95,70,0.25),0_8px_24px_-12px_rgba(6,95,70,0.12)] p-8 lg:p-10"
                data-testid="hero-demo-card"
              >
                {/* Soft branded accent line at the top — premium SaaS cue
                    without overpowering the white card surface. */}
                <div className="absolute inset-x-0 top-0 h-1 rounded-t-3xl bg-gradient-to-r from-emerald-500 via-green-600 to-emerald-500" aria-hidden="true" />

                <div className="flex items-start gap-3.5">
                  <span className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                    <Calendar className="w-6 h-6 text-emerald-700" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs tracking-[0.18em] uppercase font-semibold text-green-800">
                      {t("landing.demoCardEyebrow")}
                    </p>
                    <h2
                      className="mt-1.5 font-display text-2xl sm:text-3xl lg:text-[2rem] font-semibold text-gray-900 tracking-tight leading-tight"
                      data-testid="hero-demo-card-title"
                    >
                      {t("landing.demoCardTitle")}
                    </h2>
                  </div>
                </div>

                <p className="mt-5 text-base text-gray-700 leading-relaxed">
                  {t("landing.demoCardBody")}
                </p>

                {/* Audience positioning — small muted line directly above
                    the checklist. Reassures both solo cleaners and small
                    growing teams that the demo is calibrated for their
                    scale, not generic enterprise SaaS pitch. */}
                <p
                  className="mt-3 text-sm text-gray-500"
                  data-testid="hero-demo-card-social-context"
                >
                  {t("landing.demoCardSocialContext")}
                </p>

                {/* Two-column checklist of what the demo covers. Keeps the
                    card compact while still listing every value-driving
                    surface, so prospects know they'll get a complete tour
                    not just a feature ramble. */}
                <ul
                  className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
                  data-testid="hero-demo-card-checklist"
                >
                  {[
                    "landing.demoCard.aiReplies",
                    "landing.demoCard.aiQuotes",
                    "landing.demoCard.bookingPage",
                    "landing.demoCard.followUps",
                    "landing.demoCard.crm",
                    "landing.demoCard.liveQa",
                    "landing.demoCard.bilingual",
                  ].map((k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-gray-800">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-700 shrink-0" aria-hidden="true" />
                      <span className="leading-relaxed">{t(k)}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={DEMO_CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-8"
                  data-testid="hero-demo-card-cta"
                >
                  <Button
                    className="bg-green-800 hover:bg-green-900 text-white w-full h-14 text-base sm:text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all inline-flex items-center justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    {t("landing.demoCardCta")}
                  </Button>
                </a>

                {/* Tiny under-button reassurance — sets expectations on
                    duration and tone, reducing the "this is a hard sell"
                    fear that keeps cold prospects from clicking. */}
                <p className="mt-4 text-xs text-gray-500 text-center">
                  {t("landing.demoCardFinePrint")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "See Tasklio in Action" — single-screenshot carousel. One large,
          actually-readable product screenshot on screen at a time, with
          left/right arrows, pagination dots, keyboard navigation, and
          touch swipe support. Replaces the previous 5-card grid because
          shrunk thumbnails make the UI illegible — we'd rather show one
          beautiful frame per beat than five tiny ones at once. */}
      <ProductShowcaseCarousel t={t} />


      {/* Social proof */}
      <section className="bg-white border-b border-gray-100" data-testid="social-proof">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16 text-center">
          <p className="text-xs sm:text-sm tracking-[0.2em] uppercase font-semibold text-gray-500" data-testid="social-proof-headline">
            {t("landing.socialEyebrow")}
          </p>
          <blockquote
            className="font-display mt-5 text-xl sm:text-2xl lg:text-[1.6rem] font-medium text-gray-900 leading-snug tracking-tight"
            data-testid="social-proof-quote"
          >
            {t("landing.socialQuote")}
          </blockquote>
          <p className="mt-3 text-sm text-gray-500" data-testid="social-proof-attribution">
            {t("landing.socialAttribution")}
          </p>
          <p className="mt-6 text-xs sm:text-sm text-gray-400" data-testid="social-proof-trust">
            {t("landing.socialTrust")}
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-12 lg:pt-16 pb-20 lg:pb-28 w-full">
        {/* How it works — 3 step flow */}
        <div className="max-w-2xl mx-auto text-center">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">{t("landing.howItWorks")}</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
            {t("landing.howItWorksTitle")}
          </h2>
        </div>

        <ol
          className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 max-w-5xl mx-auto"
          data-testid="how-it-works-steps"
        >
          <li className="text-center sm:text-left flex flex-col items-center sm:items-start gap-4" data-testid="step-1">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-green-50 border border-green-100 text-green-800 text-sm font-semibold flex items-center justify-center">1</span>
              <ClipboardList className="w-5 h-5 text-green-800" />
            </div>
            <h3 className="font-display text-lg font-medium text-gray-900 leading-snug">
              {t("landing.step1")}
            </h3>
          </li>
          <li className="text-center sm:text-left flex flex-col items-center sm:items-start gap-4" data-testid="step-2">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-green-50 border border-green-100 text-green-800 text-sm font-semibold flex items-center justify-center">2</span>
              <Bell className="w-5 h-5 text-green-800" />
            </div>
            <h3 className="font-display text-lg font-medium text-gray-900 leading-snug">
              {t("landing.step2")}
            </h3>
          </li>
          <li className="text-center sm:text-left flex flex-col items-center sm:items-start gap-4" data-testid="step-3">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-green-50 border border-green-100 text-green-800 text-sm font-semibold flex items-center justify-center">3</span>
              <PhoneCall className="w-5 h-5 text-green-800" />
            </div>
            <h3 className="font-display text-lg font-medium text-gray-900 leading-snug">
              {t("landing.step3")}
            </h3>
          </li>
        </ol>

        {/* Testimonials / social proof */}
        <div className="max-w-6xl mx-auto py-16" data-testid="testimonials-section">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
              {t("landing.testimonialsTitle")}
            </h2>
            <p className="mt-4 text-gray-600 leading-relaxed">
              {t("landing.testimonialsBody")}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "I used to miss leads all the time. Now I reply within minutes and book more jobs every week.",
                name: "Sarah M.",
                location: "Atlanta, GA",
              },
              {
                quote: "The instant notifications and AI replies are a game changer. I closed multiple jobs in my first week.",
                name: "Carlos R.",
                location: "Dallas, TX",
              },
              {
                quote: "Super simple and actually works. No more chasing leads — everything is in one place.",
                name: "Emily T.",
                location: "Orlando, FL",
              },
            ].map((t, i) => (
              <div
                key={t.name}
                className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                data-testid={`testimonial-card-${i + 1}`}
              >
                <div className="flex gap-0.5 text-amber-400" aria-label="5 out of 5 stars">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="mt-4 text-gray-700 leading-relaxed">“{t.quote}”</p>
                <p className="mt-5 text-sm text-gray-500">
                  <span className="font-medium text-gray-900">{t.name}</span> — {t.location}
                </p>
              </div>
            ))}
          </div>

          <p
            className="mt-8 text-center text-sm text-gray-500"
            data-testid="testimonials-stat-line"
          >
            {t("landing.testimonialsStat")}
          </p>

          <div
            className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500"
            data-testid="testimonials-trust-row"
          >
            <span>{t("landing.trustNoContracts")}</span>
            <span className="text-gray-300">•</span>
            <span>{t("landing.trustCancel")}</span>
            <span className="text-gray-300">•</span>
            <span>{t("landing.trustStripe")}</span>
          </div>
        </div>

        <div className="mt-16 sm:mt-20 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-2xl bg-white border border-gray-200 p-8 hover:shadow-md transition-shadow" data-testid="feature-capture">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-green-800" />
              </div>
              <div>
                <h3 className="font-display text-xl font-medium text-gray-900">{t("landing.featureCaptureTitle")}</h3>
                <p className="mt-2 text-gray-600">
                  {t("landing.featureCaptureBody")}
                </p>
              </div>
            </div>
            <div className="mt-8 rounded-xl overflow-hidden border border-gray-100 aspect-[16/10] sm:aspect-[5/4]" data-testid="feature-capture-image">
              <img
                src={FEATURE_IMG}
                alt="A cleaning team working together in a bright modern home"
                className="w-full h-full object-cover scale-[1.08]"
                style={{ transformOrigin: "32% 48%" }}
                loading="lazy"
              />
            </div>
          </div>

          <div className="space-y-5">
            <div className="rounded-2xl ai-card p-7" data-testid="feature-ai-reply">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white border border-green-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-green-800" />
                </div>
                <h3 className="font-display text-lg font-medium text-gray-900">{t("landing.featureReplyTitle")}</h3>
              </div>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                {t("landing.featureReplyBody")}
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-gray-200 p-7" data-testid="feature-win-jobs">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-green-800" />
                </div>
                <h3 className="font-display text-lg font-medium text-gray-900">{t("landing.featureWinTitle")}</h3>
              </div>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                {t("landing.featureWinBody")}
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-gray-200 p-7" data-testid="feature-quote">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-sky-700" />
                </div>
                <h3 className="font-display text-lg font-medium text-gray-900">{t("landing.featureQuoteTitle")}</h3>
              </div>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                {t("landing.featureQuoteBody")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
          <div className="flex items-start gap-4" data-testid="stat-response-time">
            <div className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-green-800" />
            </div>
            <div>
              <p className="font-display text-2xl font-semibold text-gray-900">{t("landing.statResponseTitle")}</p>
              <p className="text-sm text-gray-500 mt-1">{t("landing.statResponseBody")}</p>
            </div>
          </div>
          <div className="flex items-start gap-4" data-testid="stat-conversion">
            <div className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-green-800" />
            </div>
            <div>
              <p className="font-display text-2xl font-semibold text-gray-900">{t("landing.statBookingsTitle")}</p>
              <p className="text-sm text-gray-500 mt-1">{t("landing.statBookingsBody")}</p>
            </div>
          </div>
          <div className="flex items-start gap-4" data-testid="stat-save-time">
            <div className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-green-800" />
            </div>
            <div>
              <p className="font-display text-2xl font-semibold text-gray-900">{t("landing.statSaveTitle")}</p>
              <p className="text-sm text-gray-500 mt-1">{t("landing.statSaveBody")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — placed just before the final CTA so visitors with lingering
          questions get answered without leaving the page, then are funneled
          straight into the conversion block below. */}
      <LandingFAQ />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
        <div className="rounded-3xl bg-gradient-to-br from-green-800 to-green-900 text-white p-10 sm:p-16">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("landing.ctaTitle")}
            </h2>
            <p className="mt-4 text-green-50 text-lg">
              {t("landing.ctaBody")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={primaryCtaTo}
                className="bg-white text-green-900 hover:bg-green-50 font-medium rounded-lg px-7 py-3 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="cta-register"
              >
                {user ? t("nav.goDashboard") : t("landing.ctaCreate")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/pricing#pricing"
                className="border border-white/40 text-white hover:bg-white/10 font-medium rounded-lg px-7 py-3 transition-colors inline-flex items-center justify-center"
                data-testid="cta-pricing"
              >
                {t("landing.ctaViewPricing")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Customer-facing Find a Cleaner entry point — compact band, kept
          visually quiet so the owner-focused signup CTAs stay dominant. */}
      <section className="bg-[#f6f8f6] border-y border-gray-100" data-testid="landing-find-cleaner-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg md:text-lg font-semibold text-gray-900">
              {t("landing.fcBandTitle")}
            </h2>
            <p className="mt-1 text-sm text-gray-600">{t("landing.fcBandBody")}</p>
          </div>
          <Link
            to="/find-a-cleaner"
            className="shrink-0 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-lg border border-green-800 text-green-900 hover:bg-green-50 font-semibold text-sm transition-colors"
            data-testid="landing-find-cleaner-cta"
          >
            {t("landing.fcBandCta")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />

      {/* Demo video modal — Shadcn Dialog already supplies the dark
          backdrop (bg-black/80), fade-in + zoom-in animations, ESC close,
          backdrop click close, and focus trap (via Radix). We override the
          DialogContent base classes to widen for a 16:9 video and remove
          the default 24px padding so the video extends edge-to-edge.
          Pause + rewind on close is wired through `onDemoOpenChange`. */}
      <Dialog open={demoOpen} onOpenChange={onDemoOpenChange}>
        <DialogContent
          className="max-w-4xl w-[calc(100vw-2rem)] p-0 gap-0 bg-black border-0 overflow-hidden rounded-2xl shadow-2xl"
          data-testid="demo-video-modal"
        >
          {/* Accessible title — visually hidden so the modal stays clean,
              but screen readers announce it. Required by Radix Dialog. */}
          <VisuallyHidden>
            <DialogTitle>Tasklio AI demo video</DialogTitle>
          </VisuallyHidden>
          {videoErrored ? (
            // Friendly placeholder when /demo/tasklio-demo.mp4 isn't
            // uploaded yet. Matches the 16:9 aspect ratio of the real
            // player so the modal doesn't reflow once the file ships.
            <div
              className="w-full aspect-video bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 flex flex-col items-center justify-center text-center px-6 py-10"
              data-testid="demo-video-fallback"
            >
              <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mb-5 ring-1 ring-white/20">
                <Play className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight">
                Demo video coming soon
              </h3>
              <p className="mt-3 text-sm sm:text-base text-green-50/80 max-w-md leading-relaxed">
                We&apos;re putting the finishing touches on the walkthrough. In the meantime, the fastest way to see Tasklio in action is to start your free 30-day trial — no credit card required.
              </p>
              <Button
                onClick={() => {
                  setDemoOpen(false);
                  navigate(primaryCtaTo);
                }}
                className="mt-6 bg-white text-green-900 hover:bg-green-50 h-11 px-6 text-sm font-semibold rounded-lg"
                data-testid="demo-fallback-cta"
              >
                {primaryCtaLabel} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ) : (
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              // Muted so browsers actually honor autoPlay; users can unmute
              // via the native controls.
              muted
              preload="metadata"
              controlsList="nodownload"
              onError={(e) => {
                // React StrictMode (and any unmount-while-loading) aborts
                // the in-flight media fetch which fires `error` with code
                // MEDIA_ERR_ABORTED (1). That's NOT a real load failure —
                // ignore it. Real 404 / decode errors are codes 2, 3, 4.
                const code = e?.currentTarget?.error?.code;
                if (code && code !== 1) {
                  setVideoErrored(true);
                }
              }}
              className="w-full h-auto max-h-[80vh] bg-black"
              data-testid="demo-video-player"
            >
              {/* MP4 (H.264 + AAC) — universal codec on Safari/iOS, Chrome,
                  Edge, Firefox (with system codec). WebM (VP9 + Opus) —
                  fallback for Chromium builds without H.264 license
                  (Linux Firefox without ffmpeg, Ungoogled Chromium, some
                  embedded WebViews). Browser picks the first source it
                  can decode. */}
              <source src="/demo/tasklio-demo.mp4" type="video/mp4" />
              <source src="/demo/tasklio-demo.webm" type="video/webm" />
              <track kind="captions" />
            </video>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
